import type { BuyerPublicBidDetailDto, PublicBidQualificationResponseDto } from "../../../../autogen/bff-api";
import { composeCriterionKey } from "../../../../common/qualification/criterionKey";

export interface BidQualificationProgress {
  fulfilled: number;
  total: number;
  hasRequirements: boolean;
  progress: number;
}

const responseByCriterionKey = (responses: PublicBidQualificationResponseDto[]) =>
  new Map(responses.map((response) => [response.criterionKey, response]));

export const getBidQualificationProgress = (detail: BuyerPublicBidDetailDto): BidQualificationProgress => {
  const responses = detail.bid.qualificationResponses ?? [];
  const criteria = detail.selectionCriteria ?? [];

  if (criteria.length === 0) {
    const fulfilled = responses.filter((response) => response.fulfilled === true).length;
    return {
      fulfilled,
      total: responses.length,
      hasRequirements: responses.length > 0,
      progress: responses.length > 0 ? (fulfilled / responses.length) * 100 : 0,
    };
  }

  const responsesByKey = responseByCriterionKey(responses);
  const fulfilled = criteria.filter((criterion) => responsesByKey.get(composeCriterionKey(criterion))?.fulfilled === true)
    .length;

  return {
    fulfilled,
    total: criteria.length,
    hasRequirements: true,
    progress: (fulfilled / criteria.length) * 100,
  };
};

export const getBidQualificationRows = (detail: BuyerPublicBidDetailDto): PublicBidQualificationResponseDto[] => {
  const responses = detail.bid.qualificationResponses ?? [];
  const criteria = detail.selectionCriteria ?? [];

  if (criteria.length === 0) return responses;

  const responsesByKey = responseByCriterionKey(responses);
  const criterionKeys = new Set<string>();
  const criteriaRows = criteria.map((criterion): PublicBidQualificationResponseDto => {
    const criterionKey = composeCriterionKey(criterion);
    criterionKeys.add(criterionKey);
    return responsesByKey.get(criterionKey) ?? { criterionKey, fulfilled: null, evidenceDocumentIds: [], evidenceDocuments: [] };
  });
  const extraRows = responses.filter((response) => !criterionKeys.has(response.criterionKey));

  return [...criteriaRows, ...extraRows];
};
